import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { data, plural, rotulo } from '../formato';
import { useSessao } from '../sessao';
import { Seletor, SeletorDeCliente, type OpcaoDeFiltro } from '../Filtros';
import {
  AlternarDaBarra, BarraDeLista, BuscaDaBarra, FiltrosAtivos, ResumoQueFiltra,
  RodapeDaLista, usePaginacao, type FiltroAtivo, type ItemDoResumo,
} from '../BarraDeLista';
import { Periodo, type ValorDoPeriodo } from '../Periodo';
import {
  AcaoBloqueada, Aviso, BarraDeAvanco, Carregando, ColunaOrdenavel, ErroDaApi,
  Selo, Vazio, ordenarPor, useEstreito, useOrdenacao,
} from '../ui';
import type { Tom } from '../ui';
import { STATUS, type Os } from './OsForms';
import {
  CamposDaFicha, TabelaComFicha, type ColunaComFicha, type FichaDaLinha,
} from '../TabelaComFicha';

/**
 * A linha da lista: a OS da visão MAIS as duas contagens que `GET /os`
 * junta por fora dela (247). Ficam aqui, e não no tipo `Os` da ficha,
 * porque só a lista as recebe.
 */
type OsDaLista = Os & {
  /** Recebidas que não vieram de uma amostra prevista. */
  amostras_sem_previsao?: number | string | null;
  /** Células marcadas nas amostras previstas que ainda não chegaram. */
  ensaios_por_chegar?: number | string | null;
};

/**
 * As ordens de serviço.
 *
 * A OS é o núcleo do sistema no briefing (item 5) e até 22/08/2026 era a
 * única peça central que não tinha tela nenhuma: existia no banco desde a
 * `0001`, era criada por script de carga, e o recebimento aceitava lote
 * órfão justamente para não depender dela. O efeito prático é que o
 * laboratório inteiro trabalhava sem a pergunta que amarra tudo — *a que
 * serviço este material pertence, e qual é o prazo dele?*
 *
 * A lista responde três perguntas, nesta ordem de urgência:
 *
 *   **o que está atrasado** — filtro próprio, porque é a pergunta da
 *   segunda-feira de manhã e não pode depender de ler a coluna de prazo
 *   linha por linha;
 *
 *   **o que ainda não recebeu o material que devia** — a razão de
 *   `amostras_previstas_atendidas` existir na visão: uma OS programada
 *   para doze amostras com três recebidas está esperando alguém, e isso
 *   não aparece em nenhum status;
 *
 *   **em que pé está cada uma** — os três status lado a lado, porque são
 *   independentes e um resumo único obrigaria a inventar estado.
 */

/**
 * O tom segue o ciclo de vida, como na lista de amostras: o que ainda vai
 * acontecer é neutro, o que está andando é `andamento`, o que terminou bem
 * é `ok`, e o que exige alguém agora é `atencao` ou `critico`.
 *
 * `suspensa` é `atencao` e não `neutro` de propósito: OS suspensa parece
 * inofensiva na lista e é justamente a que some do radar por meses.
 */
const TOM_TECNICO: Record<string, Tom> = {
  rascunho: 'neutro',
  aguardando_aprovacao: 'neutro',
  aguardando_programacao: 'neutro',
  programada: 'andamento',
  aguardando_amostras: 'atencao',
  amostras_recebidas: 'andamento',
  ensaios_em_andamento: 'andamento',
  aguardando_dados: 'atencao',
  em_processamento: 'andamento',
  com_pendencia: 'critico',
  concluida: 'ok',
  suspensa: 'atencao',
  cancelada: 'neutro',
};

/**
 * As situações, na lista suspensa (Testesolo, 02/09/2026).
 *
 * Até a onda 150 eram seis «chips» — os que o gestor mais usava — e
 * as outras sete situações não tinham filtro. Numa lista suspensa cabe
 * a escada inteira, na ordem em que a OS anda: é o mesmo vocabulário de
 * `STATUS.tecnico`, para o filtro não discordar da ficha.
 */
const SITUACOES: OpcaoDeFiltro[] = STATUS.tecnico
  .map((s) => ({ valor: s, texto: rotulo(s) }));

/**
 * Sobre QUAL data o intervalo pergunta.
 *
 * «filtro por intervalo de data?» — doc 48. A pergunta tem três
 * respostas plausíveis numa OS, e as três dão listas diferentes. Um
 * seletor «de/até» sem dizer sobre o quê devolve um resultado que
 * parece certo e responde outra pergunta — que é pior que não filtrar.
 */
const CAMPOS_DE_DATA = [
  { chave: 'abertura', texto: 'abertura' },
  { chave: 'prazo', texto: 'prazo' },
  { chave: 'movimentacao', texto: 'última movimentação' },
] as const;

export function OrdensDeServico() {
  const { pode, eu } = useSessao();
  /* O celular (309): cartões no lugar da tabela, sem o «Nova OS» apagado. */
  const estreito = useEstreito(640);
  /*
   * Os filtros nascem do ENDEREÇO, e não só do estado da tela.
   *
   * O Painel manda para cá com `?atrasadas=true` quando alguém toca no
   * número de atrasos. Sem ler o endereço, o link levaria à lista inteira
   * — a pessoa clicaria em "3 atrasadas" e cairia em quarenta OS, sem
   * entender que o filtro se perdeu no caminho. Vale também para o link
   * que alguém cola no grupo da equipe.
   */
  const [params, setParams] = useSearchParams();
  const [linhas, setLinhas] = useState<Os[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  /*
   * A ordenação por coluna.
   *
   * «Gostaria de incluir a ordenação com seta (crescente/decrescente)
   *  nas colunas» — Testesolo, 02/09/2026.
   *
   * É na memória porque a lista vem inteira da API. O padrão abre pelo
   * PRAZO mais apertado — é a pergunta com que se abre a lista de OS —,
   * e o prazo ordena por `dias_para_prazo`, que é o número de dias que
   * faltam: o atrasado tem o menor, e vem primeiro.
   */
  const { ordem, sentido, trocar } = useOrdenacao('col_prazo');
  const [busca, setBusca] = useState(() => params.get('busca') ?? '');
  const [status, setStatus] = useState<string | null>(
    () => params.get('status'));
  const [cliente, setCliente] = useState<string | null>(
    () => params.get('cliente'));
  /*
   * O recorte do resumo que filtra (onda 271). «Só atrasadas» e «Sem
   * prazo ou sem RT» eram botões numa faixa «e também»; viraram números
   * em cima da lista, que filtram ao clicar — e o recorte é feito aqui, na
   * lista que já chegou, para os números não encolherem quando um deles
   * está escolhido. `atrasadas=true` e `incompletas=true` continuam
   * valendo no endereço (o Painel manda para cá com eles).
   */
  const [recorte, setRecorte] = useState<string | null>(() => (
    params.get('rec') ?? (params.get('atrasadas') === 'true' ? 'atrasadas'
      : params.get('incompletas') === 'true' ? 'incompletas' : null)));
  const [minhas, setMinhas] = useState(
    () => params.get('minhas') === 'true');
  /*
   * 0288 (RN-279): «limpar a interface jogando as OSs concluídas fora do
   * sistema para algum local» — Humberto, 03/11/2026. A arquivada não
   * aparece; «Arquivadas» mostra só elas. A busca acha as duas: quem
   * digita o número de uma OS antiga quer achá-la.
   */
  const [soArquivadas, setSoArquivadas] = useState(
    () => params.get('arquivadas') === 'so');
  const podeArquivar = pode('os.arquivar');
  const [marcadas, setMarcadas] = useState<Set<string>>(new Set());
  const [selecionando, setSelecionando] = useState(false);
  const emSelecao = podeArquivar && (selecionando || marcadas.size > 0);
  const [recado, setRecado] = useState<string | null>(null);

  const [de, setDe] = useState(() => params.get('de') ?? '');
  const [ate, setAte] = useState(() => params.get('ate') ?? '');
  const [campoData, setCampoData] = useState(
    () => params.get('campoData') ?? 'abertura');

  const carregar = useCallback(async () => {
    setErro(null);
    try {
      const q = new URLSearchParams();
      if (busca) q.set('busca', busca);
      if (status) q.set('status', status);
      if (cliente) q.set('cliente', cliente);
      if (minhas) q.set('minhas', 'true');
      if (de) q.set('de', de);
      if (ate) q.set('ate', ate);
      if ((de || ate) && campoData) q.set('campoData', campoData);
      if (soArquivadas) q.set('arquivadas', 'so');
      else if (busca) q.set('arquivadas', 'incluir');
      /*
       * O endereço acompanha o filtro.
       *
       * «Filtros persistentes entre visitas e estado na URL, para poder
       *  mandar o link a um colega» — doc 48. Sem isto, quem monta um
       * recorte útil ("minhas OS atrasadas em agosto") não tem como
       * passá-lo adiante a não ser descrevendo os cliques.
       *
       * `replace` para não encher o botão Voltar do navegador com um
       * item por tecla digitada na busca.
       */
      const endereco = new URLSearchParams(q);
      if (recorte) endereco.set('rec', recorte);
      setParams(endereco, { replace: true });
      setLinhas(await api<Os[]>(`/os?${q}`));
      setMarcadas(new Set());
    } catch (e) { setErro(e); }
  }, [busca, status, cliente, minhas, de, ate, campoData, recorte, soArquivadas, setParams]);

  /* Arquivar ou desarquivar as marcadas, com um motivo só (0288). */
  const [ocupada, setOcupada] = useState(false);
  const arquivarMarcadas = async (motivo: string) => {
    setOcupada(true); setErro(null); setRecado(null);
    try {
      const rota = soArquivadas ? '/os/desarquivar' : '/os/arquivar';
      const r = await api<{ arquivadas?: number; desarquivadas?: number; puladas: number }>(
        rota, { metodo: 'POST', corpo: { ids: [...marcadas], motivo } });
      const n = r.arquivadas ?? r.desarquivadas ?? 0;
      setRecado(soArquivadas
        ? `${plural(n, 'OS desarquivada', 'OS desarquivadas')} — voltaram às filas como estavam.`
        : `${plural(n, 'OS arquivada', 'OS arquivadas')} — saíram das listas e das filas. «Arquivadas» mostra onde estão.`);
      setSelecionando(false);
      await carregar();
    } catch (e) { setErro(e); } finally { setOcupada(false); }
  };

  useEffect(() => {
    const t = setTimeout(() => { void carregar(); }, 250);
    return () => clearTimeout(t);
  }, [carregar]);


  /*
   * O que a seta ordena, coluna por coluna.
   *
   * `col_amostras` ordena pelo que FALTA chegar, e não pelo total: uma
   * OS com trinta amostras todas recebidas não é a que precisa de
   * atenção; a de doze com três é.
   */
  const emOrdem = useMemo(() => {
    const chave: Record<string, (o: Os) => string | number | null> = {
      col_os: (o) => o.numero,
      col_prazo: (o) => (o.dias_para_prazo == null
        ? null : Number(o.dias_para_prazo)),
      col_amostras: (o) => Number(o.amostras_previstas_total ?? 0)
        - Number(o.amostras_previstas_atendidas ?? 0),
      col_andamento: (o) => {
        const t = Number(o.ensaios_total ?? 0);
        /* 247: só marcada nas por chegar fica entre a sem nada e a que
           já tem ensaio — tem programação, mas nada andou. */
        if (!t) return Number((o as OsDaLista).ensaios_por_chegar ?? 0) > 0 ? 0 : -1;
        return Number(o.ensaios_aprovados ?? 0) / t;
      },
      col_situacao: (o) => rotulo(o.status_tecnico),
    };
    return ordenarPor(linhas ?? [], chave[ordem] ?? chave.col_prazo, sentido);
  }, [linhas, ordem, sentido]);

  /* O recorte do resumo, aplicado à lista que já chegou. */
  const RECORTES_DA_OS: (ItemDoResumo & { f(o: Os): boolean })[] = [
    { chave: 'atrasadas', rotulo: 'Atrasadas', aux: 'o prazo contratual passou', tom: 'critico',
      numero: 0, f: (o) => !!o.atrasada },
    { chave: 'vencem', rotulo: 'Vencem em 7 dias', aux: 'prazo até a semana que vem', tom: 'atencao',
      numero: 0,
      f: (o) => !o.atrasada && o.dias_para_prazo != null && Number(o.dias_para_prazo) >= 0
        && Number(o.dias_para_prazo) <= 7 && !['concluida', 'cancelada'].includes(o.status_tecnico) },
    { chave: 'amostras', rotulo: 'Aguardando amostras', aux: 'nada ou parte chegou', tom: 'andamento',
      numero: 0, f: (o) => o.status_tecnico === 'aguardando_amostras' },
    { chave: 'incompletas', rotulo: 'Sem prazo ou sem RT', aux: 'em andamento, falta cadastro', tom: 'atencao',
      numero: 0,
      f: (o) => !['rascunho', 'concluida', 'cancelada', 'suspensa'].includes(o.status_tecnico)
        && (!o.prazo_contratual || !o.responsavel_tecnico_id) },
  ];
  const rec = RECORTES_DA_OS.find((r) => r.chave === recorte) ?? null;
  const resumo = RECORTES_DA_OS.map((r) => ({ ...r, numero: (linhas ?? []).filter(r.f).length }));
  const recortadas = rec ? emOrdem.filter(rec.f) : emOrdem;
  /* A pergunta da lista: mudou, o celular recomeça em 30. */
  const pag = usePaginacao(recortadas, 50,
    [busca, status, cliente, minhas, de, ate, campoData, recorte].join('|'));

  const periodo: ValorDoPeriodo = { de, ate };
  const ativos: FiltroAtivo[] = [];
  if (rec) ativos.push({ chave: 'rec', texto: rec.rotulo, tirar: () => setRecorte(null) });
  if (status) ativos.push({ chave: 'st', texto: `Situação: ${rotulo(status)}`, tirar: () => setStatus(null) });
  if (cliente) ativos.push({ chave: 'cli', texto: 'Cliente escolhido', tirar: () => setCliente(null) });
  if (minhas) ativos.push({ chave: 'min', texto: 'Minhas OS', tirar: () => setMinhas(false) });
  if (busca) ativos.push({ chave: 'q', texto: `Busca: ${busca}`, tirar: () => setBusca('') });
  if (soArquivadas) ativos.push({ chave: 'arq', texto: 'Só as arquivadas', tirar: () => setSoArquivadas(false) });
  if (de || ate) {
    ativos.push({
      chave: 'per',
      texto: `${CAMPOS_DE_DATA.find((c) => c.chave === campoData)?.texto ?? 'data'}: ${de ? data(de) : '…'} – ${ate ? data(ate) : '…'}`,
      tirar: () => { setDe(''); setAte(''); },
    });
  }
  /* O «Limpar filtros» da faixa: tudo. */
  const limparFiltros = () => {
    setRecorte(null); setStatus(null); setCliente(null); setMinhas(false);
    setBusca(''); setDe(''); setAte(''); setSoArquivadas(false);
  };
  /* O número e o «Limpar» da folha do celular (309, Ruling R13): só o que
     mora na folha — a busca e a pílula estão à vista e ficam. */
  const naFolha = ativos.filter((f) => ['st', 'cli', 'min', 'per'].includes(f.chave)).length;
  const limparDaFolha = () => {
    setStatus(null); setCliente(null); setMinhas(false); setDe(''); setAte('');
  };
  const ORDENS: Record<string, string> = {
    col_os: 'número', col_prazo: 'prazo', col_amostras: 'amostras que faltam',
    col_andamento: 'andamento', col_situacao: 'situação',
  };

  return (
    <div>
      <div className="cabeca cabeca-lista">
        <div className="cresce">
          <h1>Ordens de serviço</h1>
          <p className="sub">
            O que foi contratado, para quem, e para quando — o prazo mais próximo primeiro
          </p>
        </div>
        {/*
          A ação primária fica no cabeçalho, sempre à vista: a lista vazia
          — primeiro dia de uso, ou filtro que não achou nada — é
          justamente onde abrir uma OS faz mais falta.
        */}
        {/* No celular (309) o botão apagado não ocupa a primeira tela de
            quem não abre OS: o motivo continua no desktop (146.20). */}
        {pode('os.criar') ? (
          <Link to="/os/nova" className="botao">Nova OS</Link>
        ) : !estreito && (
          <AcaoBloqueada
            texto="Nova OS"
            oQueFaz="não abre ordem de serviço"
            permissao="Abrir ordem de serviço"
            persona={eu?.atuandoPor ? eu.nome : null}
          />
        )}
      </div>

      <ResumoQueFiltra rotulo="Resumo das ordens de serviço" itens={resumo}
        ativo={recorte} aoEscolher={setRecorte} />

      <BarraDeLista rotulo="Filtros das ordens de serviço" ativos={naFolha}
        aoLimpar={limparDaFolha} total={linhas ? recortadas.length : undefined}>
        <BuscaDaBarra valor={busca} aoMudar={setBusca}
          placeholder="Número da obra (7 dígitos), obra ou cliente"
          rotulo="Buscar ordem de serviço" />
        <Periodo valor={periodo}
          aoMudar={(v) => { setDe(v.de); setAte(v.ate); }}
          campos={CAMPOS_DE_DATA.map((c) => ({ chave: c.chave, texto: c.texto }))}
          campo={campoData} aoMudarCampo={setCampoData}
          atalhos={['mes', 'mesPassado', 'proximos7', 'ultimos30', 'tudo']} />
        <Seletor naBarra rotulo="Situação" valor={status} opcoes={SITUACOES}
          aoMudar={setStatus} todos="Todas as situações" />
        <SeletorDeCliente naBarra valor={cliente} aoMudar={setCliente} />
        {/*
          «Minhas OS» — o filtro que ele não pediu e que é provavelmente o
          mais usado (doc 48): o RT ou quem abriu. É pergunta do banco
          (a trilha diz quem abriu), por isso continua indo à API.
        */}
        <AlternarDaBarra ligado={minhas} aoMudar={setMinhas}>
          {minhas ? '✓ Minhas OS' : 'Minhas OS'}
        </AlternarDaBarra>
        {/* 0288: as arquivadas não aparecem; aqui, só elas. */}
        <AlternarDaBarra ligado={soArquivadas} aoMudar={setSoArquivadas}>
          {soArquivadas ? '✓ Arquivadas' : 'Arquivadas'}
        </AlternarDaBarra>
      </BarraDeLista>

      <FiltrosAtivos itens={ativos} aoLimpar={limparFiltros}
        contagem={linhas ? `${recortadas.length} de ${linhas.length} OS` : undefined}
        acao={podeArquivar && estreito && linhas && (recortadas.length > 0 || emSelecao) ? (
          <button type="button" className="botao secundario botao-selecionar"
            onClick={() => {
              if (emSelecao) { setSelecionando(false); setMarcadas(new Set()); }
              else setSelecionando(true);
            }}>
            {emSelecao ? 'Cancelar seleção' : 'Selecionar'}
          </button>
        ) : undefined} />

      {recado && (
        <Aviso tipo="ok">
          {recado}{' '}
          <button type="button" className="botao discreto"
            onClick={() => setRecado(null)}>Fechar</button>
        </Aviso>
      )}

      {erro ? <ErroDaApi erro={erro} />
        : !linhas ? <Carregando />
        : (
          <div className="cartao">
            {marcadas.size > 0 && (
              <BarraDeArquivo quantas={marcadas.size} desarquivar={soArquivadas}
                ocupada={ocupada} aoLimpar={() => setMarcadas(new Set())}
                aoConfirmar={arquivarMarcadas} />
            )}
            {recortadas.length === 0 ? (
              <Vazio>
                {soArquivadas && linhas.length === 0 && ativos.length === 1
                  ? 'Nenhuma OS arquivada. Para arquivar, marque as OS na lista e use «Arquivar as marcadas».'
                  : linhas.length > 0 || ativos.length > 0
                  ? 'Nenhuma ordem de serviço com estes filtros. Use «Limpar filtros» para ver todas.'
                  : pode('os.criar')
                    ? 'Nenhuma ordem de serviço ainda. A OS nasce da obra: escolha a obra e o número dela é o número da OS.'
                    : 'Nenhuma ordem de serviço. Seu perfil não abre OS — peça a quem faz a programação.'}
              </Vazio>
            ) : (
              <TabelaComFicha
                linhas={pag.visiveis}
                chave={(o) => o.id}
                nome={(o) => `OS ${o.numero}`}
                rotulo="Ordens de serviço"
                colunas={colunasDaOs({
                  ordem, sentido, trocar,
                  marcar: podeArquivar && (!estreito || emSelecao)
                    ? { linhas: pag.visiveis, marcadas, setMarcadas } : null,
                })}
                classeDaLinha={(o) => (marcadas.has(o.id) ? 'marcada' : undefined)}
                marca={emSelecao ? (o) => (
                  <CaixaDaOs o={o} marcadas={marcadas} setMarcadas={setMarcadas} />
                ) : undefined}
                ficha={(o) => fichaDaOs(o, pode('os.programar'), pode('amostra.receber'))}
                lixeira={{ tipo: 'os', id: (o) => o.id, rotulo: (o) => `OS ${o.numero}`,
                  aoApagar: () => { void carregar(); } }}
                cartao={(o) => <CartaoDaOs os={o} />}
                destino={(o) => `/os/${o.id}`}
              />
            )}
            <RodapeDaLista {...pag} teto={500}
              ordem={`${ORDENS[ordem] ?? 'prazo'} ${sentido === 'desc' ? '↓' : '↑'}`} />

            <p className="rodape-regra">
              O número da OS é o número do projeto da obra — sete dígitos,
              como <span className="mono">2608194</span>: ano, mês de abertura
              e sequencial. Uma obra tem uma OS, e o banco garante isso: não
              existe segunda OS para a mesma obra.
            </p>
          </div>
        )}
    </div>
  );
}

/**
 * Amostras: quantas de quantas, e **desde quando**.
 *
 * «data de chegada da amostra vai dentro da célula de amostras, não em
 *  coluna nova» — doc 48. E as duas frases pedem ações opostas:
 *
 *   `0 de 6 · esperando há 9 dias`  → alguém tem que ligar para o cliente
 *   `4 de 6 · última em 21/08`      → alguém tem que cobrar o resto
 *
 * Uma coluna de data pura daria a mesma célula às duas, e a diferença
 * entre elas é justamente o que faz a lista servir para alguma coisa.
 *
 * ── A amostra que chegou sem ter sido prevista (247) ─────────────────
 *
 * «Ensaios que não estão homologados não estão sendo listados na
 *  programação, mas deveriam. Temos que começar a rodar as OS por
 *  completo» — Humberto, 02/10/2026, com esta lista ao lado da
 * programação da OS 2610363. A célula dizia «sem programação» em toda OS
 * sem amostra PREVISTA, e a 2610363 tem 30 CPs recebidos com 30 MR.Q15
 * programados: o MR.Q15 ainda não tem conta homologada, e a frase pareceu
 * dizer que ele tinha ficado de fora. Não tinha — quem estava de fora era a
 * amostra recebida direto, sem previsão, que a célula não contava.
 *
 * Agora a célula conta o que chegou, previsto ou não, e só diz «nenhuma
 * amostra prevista» quando de fato não há nada — nem previsto, nem
 * recebido. «Programação» é a palavra da grade de ensaios; aqui não.
 */
function Amostras({ os }: { os: OsDaLista }) {
  const previstas = Number(os.amostras_previstas_total ?? 0);
  const recebidas = Number(os.amostras_recebidas ?? 0);
  const semPrevisao = Number(os.amostras_sem_previsao ?? 0);

  if (previstas === 0) {
    if (recebidas === 0) {
      return <span className="nada">nenhuma amostra prevista</span>;
    }
    return (
      <>
        <span className="mono">{recebidas}</span>
        <span className="sub">recebida(s)</span>
        {os.ultima_chegada && (
          <span className="meta">última em {data(os.ultima_chegada)}</span>
        )}
      </>
    );
  }
  const atendidas = Number(os.amostras_previstas_atendidas ?? 0);
  const parada = Number(os.dias_parada ?? 0);

  return (
    <>
      <span className="mono">{atendidas} de {previstas}</span>
      <span className="sub">recebidas</span>
      {/* A que chegou fora da previsão não entra no «de N», mas existe. */}
      {semPrevisao > 0 && (
        <span className="sub">+ {semPrevisao} sem previsão</span>
      )}
      {os.ultima_chegada ? (
        <span className="meta">última em {data(os.ultima_chegada)}</span>
      ) : recebidas === 0 ? (
        /*
         * Nada chegou. O número que importa aqui não é a data (não há
         * uma), é o TEMPO: uma OS esperando material há nove dias é uma
         * conversa com o cliente; há um dia, não é nada.
         *
         * 247: só quando nada chegou MESMO. A data vem do lote de
         * recebimento; amostra que entrou sem lote (carga antiga) não
         * tem data, mas chegou — e «nada chegou ainda» ao lado de «1 de
         * 2 recebidas» seria a célula se contradizendo.
         */
        <span className="sub aviso-linha">
          {parada > 0 ? `esperando há ${parada} dia(s)` : 'nada chegou ainda'}
        </span>
      ) : null}
    </>
  );
}

/**
 * O avanço físico — barra fina e o número por extenso.
 *
 * ── A separação que precisa ficar clara (doc 48) ──────────────────────
 *
 * Isto é progresso **operacional**: quanto do trabalho andou. Não é
 * medição. Progresso para fins de medição é quantitativo apurado e vive
 * no módulo de medição (§13).
 *
 * Os dois números vão divergir sempre — a diferença entre eles é
 * exatamente o executado ainda não medido, que é uma grandeza legítima e
 * útil. Mas se aparecerem lado a lado sem rótulo, a tela vira fonte de
 * confusão: alguém fecha uma medição pelo número errado.
 *
 * Por isso o cabeçalho diz «Andamento» e não «Progresso», e o texto diz
 * «ensaios».
 */
function Andamento({ os }: { os: OsDaLista }) {
  const total = Number(os.ensaios_total ?? 0);
  /*
   * 247: o ensaio marcado numa amostra que ainda não chegou também é
   * programação — ele vira ensaio sozinho na chegada (0161) e já consome
   * o escopo. «Sem ensaio programado» numa OS com dezenas de células
   * marcadas esperando o material era a mesma mentira da coluna ao lado.
   */
  const porChegar = Number(os.ensaios_por_chegar ?? 0);
  if (total === 0) {
    if (porChegar > 0) {
      return (
        <div className="avanco">
          <span className="mono">{porChegar} ensaio(s)</span>
          <span className="sub">marcados nas amostras por chegar</span>
        </div>
      );
    }
    return <span className="nada">sem ensaio programado</span>;
  }
  const pct = os.avanco_pct === null || os.avanco_pct === undefined
    ? null : Number(os.avanco_pct);
  const aprovados = Number(os.ensaios_aprovados ?? 0);
  const devolvidos = Number(os.ensaios_devolvidos ?? 0);

  return (
    <div className="avanco">
      {pct !== null && (
        <BarraDeAvanco pct={pct}
          rotulo={`Avanço dos ensaios da OS ${os.numero}`} />
      )}
      <span className="mono">
        {aprovados} de {total} ensaio(s)
      </span>
      {porChegar > 0 && (
        <span className="sub">+ {porChegar} nas amostras por chegar</span>
      )}
      {devolvidos > 0 && (
        <span className="sub aviso-linha">
          {devolvidos} devolvido(s) para correção
        </span>
      )}
    </div>
  );
}

/**
 * Há quanto tempo esta OS está parada onde está.
 *
 * «O que falta e importa é quantos dias a OS está parada na situação
 *  atual» — doc 48. A data de abertura já está no número da OS
 * (2608194 = ano 26, mês 08); o que o número não diz é se ela andou
 * desde então.
 *
 * O piso de sete dias é deliberado: uma OS que mudou de situação ontem
 * não tem nada a dizer, e escrever «há 1 dia» em quarenta linhas ensina
 * a ignorar a coluna — que é o mesmo motivo de «não iniciado» não
 * aparecer aqui.
 */
function Parada({ os }: { os: Os }) {
  const dias = Number(os.dias_parada ?? 0);
  if (!Number.isFinite(dias) || dias < 7) return null;
  const grave = dias >= 30;
  return (
    <span className={`sub ${grave ? 'aviso-linha' : ''}`}>
      {grave ? 'parada' : 'nesta situação'} há {dias} dias
    </span>
  );
}

/**
 * O prazo em palavras, não em data pura.
 *
 * "12/09/2026" obriga quem lê a fazer a conta de cabeça, linha por linha,
 * e é assim que um atraso de dois dias passa despercebido numa lista de
 * quarenta. A data continua ali — o que muda é que a conta já vem feita.
 */
function Prazo({ os }: { os: Os }) {
  if (!os.prazo_contratual) {
    return <span className="nada">sem prazo contratual</span>;
  }
  const dias = os.dias_para_prazo === null || os.dias_para_prazo === undefined
    ? null : Number(os.dias_para_prazo);

  return (
    <>
      <span className="mono">{data(os.prazo_contratual)}</span>
      {os.atrasada ? (
        <span className="sub aviso-linha">
          {dias !== null ? `atrasada há ${-dias} dia(s)` : 'atrasada'}
        </span>
      ) : dias !== null && dias <= 7 ? (
        <span className="sub">
          {dias === 0 ? 'vence hoje' : `faltam ${dias} dia(s)`}
        </span>
      ) : null}
    </>
  );
}

/**
 * O cartão da OS no celular (309, spec 06 §6 item 6).
 *
 * O que as colunas que ficam na tabela dizem, numa leitura de cima para
 * baixo: o número e a situação inteira (o selo não corta a palavra), o
 * cliente, a obra numa linha, o prazo com a conta já feita e o andamento.
 * Os mesmos campos que `colunasDaOs` lê — o cartão muda a forma, não o que
 * a lista diz.
 */
function CartaoDaOs({ os }: { os: OsDaLista }) {
  return (
    <>
      <div className="cartao-l1">
        <span className="mono">{os.numero}</span>
        <span className="cartao-selos">
          <Selo tom={TOM_TECNICO[os.status_tecnico] ?? 'neutro'}>
            {rotulo(os.status_tecnico)}
          </Selo>
          {os.demonstracao && <Selo tom="atencao">Demonstração</Selo>}
        </span>
      </div>
      <span className="cartao-cli">{os.cliente ?? 'sem cliente'}</span>
      {os.obra_nome && <span className="cartao-meta">{os.obra_nome}</span>}
      <span className="cartao-meta"><PrazoNoCartao os={os} /></span>
      <div className="cartao-andamento"><AndamentoNoCartao os={os} /></div>
    </>
  );
}

/**
 * O prazo do cartão: «prazo 12/10 · faltam 4 d», «prazo 02/10 · 6 d
 * atrasada». A mesma conta do `Prazo` da tabela, escrita curta — e sempre:
 * no cartão não há a coluna de data ao lado para dizer o resto.
 */
function PrazoNoCartao({ os }: { os: Os }) {
  if (!os.prazo_contratual) return <span className="nada">sem prazo contratual</span>;
  const dias = os.dias_para_prazo === null || os.dias_para_prazo === undefined
    ? null : Number(os.dias_para_prazo);
  const quando = `prazo ${data(os.prazo_contratual).slice(0, 5)}`;
  if (os.atrasada) {
    return (
      <>
        {quando} · <span className="aviso-linha">
          {dias !== null ? `${-dias} d atrasada` : 'atrasada'}
        </span>
      </>
    );
  }
  if (dias === null) return <>{quando}</>;
  return <>{quando} · {dias === 0 ? 'vence hoje' : `faltam ${dias} d`}</>;
}

/** O andamento do cartão: a barra e «X de Y ensaios aprovados». */
function AndamentoNoCartao({ os }: { os: OsDaLista }) {
  const total = Number(os.ensaios_total ?? 0);
  if (total === 0) {
    const porChegar = Number(os.ensaios_por_chegar ?? 0);
    return (
      <span className="nada">
        {porChegar > 0
          ? `${plural(porChegar, 'ensaio marcado', 'ensaios marcados')} nas amostras por chegar`
          : 'sem ensaio programado'}
      </span>
    );
  }
  const pct = os.avanco_pct === null || os.avanco_pct === undefined
    ? null : Number(os.avanco_pct);
  return (
    <>
      {pct !== null && (
        <BarraDeAvanco pct={pct} rotulo={`Avanço dos ensaios da OS ${os.numero}`} />
      )}
      <span>{Number(os.ensaios_aprovados ?? 0)} de {plural(total, 'ensaio aprovado', 'ensaios aprovados')}</span>
    </>
  );
}

/**
 * As colunas da lista de OS (onda 212). A OS, o prazo e a situação ficam
 * sempre — são o que se procura e o que decide o que fazer; amostras e
 * andamento são detalhe e, com a ficha aberta, moram nela.
 */
type Marcar = {
  linhas: Os[];
  marcadas: Set<string>;
  setMarcadas: (f: (m: Set<string>) => Set<string>) => void;
};

function colunasDaOs({ ordem, sentido, trocar, marcar }: {
  ordem: string; sentido: 'asc' | 'desc'; trocar: (campo: string) => void;
  marcar: Marcar | null;
}): ColunaComFicha<Os>[] {
  const col = (campo: string, texto: string, opcional = false) => (
    <ColunaOrdenavel campo={campo} texto={texto} opcional={opcional}
      ordem={ordem} sentido={sentido} aoTrocar={trocar} />
  );
  /* 0288: a caixa de marcar, só para quem arquiva. Marca as VISÍVEIS. */
  const caixa: ColunaComFicha<Os>[] = marcar ? [{
    chave: 'marca',
    cabeca: (
      <th scope="col" className="col-marca">
        <input type="checkbox" aria-label="Marcar todas as OS da lista"
          checked={marcar.linhas.length > 0 && marcar.linhas.every((o) => marcar.marcadas.has(o.id))}
          onChange={(e) => {
            const ligado = e.currentTarget.checked;
            marcar.setMarcadas(() => (ligado ? new Set(marcar.linhas.map((o) => o.id)) : new Set()));
          }} />
      </th>
    ),
    celula: (o) => (
      <td className="col-marca">
        <CaixaDaOs o={o} marcadas={marcar.marcadas} setMarcadas={marcar.setMarcadas} />
      </td>
    ),
  }] : [];
  return [
    ...caixa,
    {
      chave: 'os',
      cabeca: col('col_os', 'OS'),
      celula: (o) => (
        <th scope="row">
          <Link to={`/os/${o.id}`}>
            <strong className="mono">{o.numero}</strong>
          </Link>
          <span className="sub">{o.obra_nome}</span>
          <span className="sub">{o.cliente}</span>
          {(o.municipio || o.uf) && (
            <span className="meta">
              {[o.municipio, o.uf].filter(Boolean).join(' — ')}
            </span>
          )}
        </th>
      ),
    },
    { chave: 'prazo', cabeca: col('col_prazo', 'Prazo'), celula: (o) => <td><Prazo os={o} /></td> },
    {
      chave: 'amostras', detalhe: true,
      cabeca: col('col_amostras', 'Amostras', true),
      celula: (o) => <td className="col-opcional"><Amostras os={o} /></td>,
    },
    {
      /* Andamento é `col-opcional` pelo mesmo motivo de Amostras: no
         celular a tabela rola, e a coluna empurrava SITUAÇÃO para fora. */
      chave: 'andamento', detalhe: true,
      cabeca: col('col_andamento', 'Andamento', true),
      celula: (o) => <td className="col-opcional"><Andamento os={o} /></td>,
    },
    {
      chave: 'situacao',
      cabeca: col('col_situacao', 'Situação'),
      celula: (o) => (
        <td>
          <Selo tom={TOM_TECNICO[o.status_tecnico] ?? 'neutro'}>
            {rotulo(o.status_tecnico)}
          </Selo>
          {o.demonstracao && <Selo tom="atencao">Demonstração</Selo>}
          {o.arquivada && <Selo tom="neutro">Arquivada</Selo>}
          <Parada os={o} />
          {/* Relatório e medição só quando saíram do zero: «não iniciado»
              em toda linha é ruído que ensina a ignorar a coluna. */}
          {o.status_relatorio !== 'nao_iniciado' && (
            <span className="sub">Relatório: {rotulo(o.status_relatorio)}</span>
          )}
          {o.status_medicao !== 'nao_iniciada' && (
            <span className="sub">Medição: {rotulo(o.status_medicao)}</span>
          )}
        </td>
      ),
    },
  ];
}

/** A ficha da OS ao lado da lista: o que as colunas recolhidas diziam, e mais. */
function fichaDaOs(o: Os, podeProgramar: boolean, podeReceber: boolean): FichaDaLinha {
  const cancelada = o.status_tecnico === 'cancelada' || o.cancelada;
  return {
    rotulo: 'Ordem de serviço',
    titulo: o.numero,
    tituloCodigo: true,
    sub: (
      <>
        <span>{o.cliente}</span>
        <span>
          {[o.obra_nome, [o.municipio, o.uf].filter(Boolean).join(' — ')]
            .filter(Boolean).join(' · ')}
        </span>
      </>
    ),
    corpo: (
      <>
        <div className="tf-selos">
          <Selo tom={TOM_TECNICO[o.status_tecnico] ?? 'neutro'}>
            {rotulo(o.status_tecnico)}
          </Selo>
          {o.demonstracao && <Selo tom="atencao">Demonstração</Selo>}
          {o.arquivada && <Selo tom="neutro">Arquivada</Selo>}
        </div>
        {o.arquivada && (
          <Aviso tipo="info"
            titulo={`Arquivada em ${data(o.arquivada_em)}${o.arquivada_por_nome ? ` por ${o.arquivada_por_nome}` : ''}`}>
            {o.arquivada_motivo}
          </Aviso>
        )}
        {o.atrasada && !o.arquivada && (
          <div className="tf-aviso critico">
            <strong>Prazo vencido em {data(o.prazo_contratual)}.</strong>
          </div>
        )}
        <CamposDaFicha itens={[
          ['Prazo', <Prazo os={o} />],
          ['Amostras', <Amostras os={o} />],
          ['Andamento', <Andamento os={o} />],
          ['Relatório', rotulo(o.status_relatorio)],
          ['Medição', rotulo(o.status_medicao)],
          ['Responsável técnico', o.responsavel_tecnico_nome
            ?? <span className="nada">não definido</span>],
          ['Aberta em', data(o.criado_em)],
          ['Proposta', o.proposta_numero],
        ]} />
      </>
    ),
    acoes: (
      <>
        {/* 281: receber direto da lista, já com a OS e as previstas dela. */}
        {podeReceber && !cancelada && !o.suspensa && !o.arquivada && (
          <Link className="botao secundario tf-acao-larga"
            to={`/recebimento/novo?os=${o.id}`}>Receber amostras</Link>
        )}
        {podeProgramar && !cancelada && !o.arquivada && (
          <Link className="botao secundario" to={`/os/${o.id}/programacao`}>Programar</Link>
        )}
        <Link className="botao" to={`/os/${o.id}`}>Abrir OS</Link>
      </>
    ),
  };
}

/** A caixa de marcar uma OS: na coluna da tabela e ao lado do cartão do celular. */
function CaixaDaOs({ o, marcadas, setMarcadas }: {
  o: Os;
  marcadas: Set<string>;
  setMarcadas: (f: (m: Set<string>) => Set<string>) => void;
}) {
  return (
    <input type="checkbox" aria-label={`Marcar OS ${o.numero}`}
      checked={marcadas.has(o.id)}
      onChange={(e) => {
        /* Lido ANTES do atualizador: o evento se anula quando o manipulador volta. */
        const ligado = e.currentTarget.checked;
        setMarcadas((m) => {
          const n = new Set(m);
          if (ligado) n.add(o.id); else n.delete(o.id);
          return n;
        });
      }} />
  );
}

/**
 * Arquivar as marcadas (0288, RN-279) — ou desarquivar, quando a lista
 * mostra as arquivadas. O motivo é obrigatório e vai para o histórico de
 * cada OS: «relatório entregue em Excel», «obra encerrada em 2025».
 * Grudada no topo do quadro, como a barra de lote das amostras.
 */
function BarraDeArquivo({ quantas, desarquivar, ocupada, aoLimpar, aoConfirmar }: {
  quantas: number;
  desarquivar: boolean;
  ocupada: boolean;
  aoLimpar(): void;
  aoConfirmar(motivo: string): void | Promise<void>;
}) {
  const [motivo, setMotivo] = useState('');
  const curto = motivo.trim().length < 10;
  return (
    <div className="barra-lote" role="region"
      aria-label={desarquivar ? 'Desarquivar as OS marcadas' : 'Arquivar as OS marcadas'}>
      <strong>{plural(quantas, 'OS marcada', 'OS marcadas')}</strong>
      <input type="text" value={motivo} maxLength={300}
        placeholder={desarquivar ? 'por quê: volta a ser trabalhada no sistema'
          : 'por quê: relatório entregue pelo Excel'}
        aria-label={desarquivar ? 'Motivo de desarquivar' : 'Motivo de arquivar'}
        onChange={(e) => setMotivo(e.target.value)} />
      <button type="button" className="botao" disabled={curto || ocupada}
        title={curto ? 'Escreva o motivo (10 letras ou mais)' : undefined}
        onClick={() => aoConfirmar(motivo.trim())}>
        {ocupada ? 'Gravando…' : desarquivar ? 'Desarquivar as marcadas' : 'Arquivar as marcadas'}
      </button>
      <button type="button" className="botao discreto" onClick={aoLimpar}>
        Desmarcar
      </button>
      {!desarquivar && (
        <span className="sub">
          Some das listas e das filas e fica só leitura. Nada é apagado.
        </span>
      )}
    </div>
  );
}
